import React, { useState } from "react";
import uploadFineNoticeStyle from "./uploadFineNoticeStyle";
import { Navigate, useLocation, useNavigate } from "react-router";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import ApiClient from "@/src/services/apiClient";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import { dateFieldDefault, isTodayOrPastDate, requiredValidator } from "@/src/services/validationRules";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { FormProvider, useForm } from "@/src/provider/Form/FormProvider";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import { Box, Stack, Typography } from "@mui/material";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import InfoIconButton from "@/src/components/Button/InfoIconButton";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

type TrafficFineNoticeFormProps = {
  handleInfoModalOpen: () => void;
  isLoading: boolean;
  submitDisabled: boolean;
};

const TrafficFineNoticeForm = ({ handleInfoModalOpen, isLoading, submitDisabled }: TrafficFineNoticeFormProps) => {
  const { handleSubmit, values } = useForm();
  return (
    <form onSubmit={handleSubmit}>
      <Stack
        direction={"row"}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "0.5rem",
        }}
      >
        <ValidatorDateField
          name="fineNoticeReceivedDate"
          label="Bescheid erhalten am"
          validators={[
            requiredValidator("Bitte geben Sie an, wann Sie den Bußgeldbescheid erhalten haben."),
            dateFieldDefault,
            { validator: isTodayOrPastDate, message: "Das Datum darf nicht in der Zukunft liegen." },
          ]}
        />
        <InfoIconButton onClick={handleInfoModalOpen} sx={{ height: "40px", width: "40px", margin: "10px" }} />
      </Stack>
      <ButtonLoading
        sx={uploadFineNoticeStyle.button}
        type={"submit"}
        fullWidth
        variant={"contained"}
        color={"primary"}
        isLoading={isLoading}
        disabled={!values.fineNoticeReceivedDate || submitDisabled}
      >
        Dokument hochladen
      </ButtonLoading>
    </form>
  );
};

type UploadFineNoticeProps = {
  traffic: AbstractCase;
};

export default function UploadFineNotice({ traffic }: UploadFineNoticeProps) {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useProductParams();
  const productApiUri = getProductData("traffic", "apiUri");
  const queryClient = useQueryClient();

  let mediaObjectCombinationRequestTypes = ["fineNoticeFiles"];

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction, hasAtLeastOneFileOfType } = useMediaObjectCombinationRequest(
    traffic,
    mediaObjectCombinationRequestTypes,
  );

  if (isLoadingMediaObjectCombinationRequest) {
    return <LegalbirdLoader />;
  }

  if (traffic.fineNoticeReceivedDate) {
    return <Navigate to={getCaseLink(traffic)} />;
  }

  const handleSubmit = async ({ values }: { values: FormDataTypes["values"] }) => {
    setIsLoadingInternal(true);
    ApiClient.post(`/generate_finenotice_mediaobject/traffic/${traffic.id}`, {
      body: JSON.stringify({
        fineNoticeReceivedDate: values.fineNoticeReceivedDate,
      }),
    }).then(
      () =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.item(productApiUri, productId),
        }),
      () => setIsLoadingInternal(false),
    );
  };

  const handleInfoModalOpen = () => {
    navigate(location.pathname, { state: { infoModalOpen: true } });
  };

  const handleModalClose = () => {
    navigate(-1);
  };

  return (
    <>
      <ContentBox
        headline={<strong>Bußgeldbescheid jetzt abschicken</strong>}
        headlineVariant={"h5"}
        showDivider={true}
        sx={(theme) => ({ textAlign: "left", "& h2": { color: theme.palette.text.primary } })}
      >
        <Typography sx={{ marginBottom: "1.5rem" }}>
          Laden Sie hier Ihren Bußgeldbescheid als Bild oder PDF hoch, oder fotografieren Sie ihn einfach ab. Bitte beachten Sie dabei, dass alles gut erkennbar
          und nichts abgeschnitten ist. Benutzen Sie bestenfalls beim Fotografieren den Blitz.
        </Typography>
        <Box>
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests.fineNoticeFiles}
            temporaryMediaObjectsFieldname={"fineNoticeFiles"}
            productEntity={traffic}
            productEntityId={traffic.id}
            product={"traffic"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            fieldName={"fineNoticeFiles"}
            uploadFileLabel={"Datei auswählen"}
          />
        </Box>
        <Typography sx={{ marginTop: "1rem" }}>Wann haben Sie den Bußgeldbescheid erhalten?</Typography>
        <FormProvider initialValues={{ fineNoticeReceivedDate: "" }} submitAction={handleSubmit}>
          <TrafficFineNoticeForm
            handleInfoModalOpen={handleInfoModalOpen}
            isLoading={isLoadingInternal}
            submitDisabled={!hasAtLeastOneFileOfType(mediaObjectCombinationRequests, "fineNoticeFiles")}
          />
        </FormProvider>
      </ContentBox>
      <DialogModal open={!!(location.state && location.state.infoModalOpen)} onClose={handleModalClose} title={"Datum der Zustellung"}>
        <p>
          Wichtig: Hier geht es darum, wann Ihnen der Brief (meist in gelbem Umschlag) von der Post zugestellt wurde. Auch wenn Sie zum Beispiel den Brief erst
          eine Woche nachdem er im Briefkasten lag geöffnet haben, gilt hier aus rechtlicher Sicht immer das Datum der Zustellung. Dieses Datum ist sehr
          wichtig, denn gegen einen Bußgeldbescheid kann nur innerhalb von zwei Wochen nach Zustellung bei Ihnen Einspruch eingelegt werden. Wird der Einspruch
          nicht innerhalb der Frist eingelegt, ist er unwirksam und wird von der Behörde als unzulässig verworfen.
        </p>
      </DialogModal>
    </>
  );
}
